import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, hasSession, type Account, type AuthConfig, type Chat } from '../lib/api';
import { Profile } from './Profile';
import { HowTo } from './HowTo';
import { ChatSearch } from './ChatSearch';
import { ChatTitle } from './ChatTitle';
import { SignIn } from './SignIn';
import { ChatList } from './ChatList';
import { Rail, type Panel } from './Rail';
import { MediaPanel } from './MediaPanel';
import { AskPanel, type Thread } from './AskPanel';
import { openPreview, type Preview } from '../lib/preview';
import { takeSharedFile } from '../lib/shareinbox';
import { watchForUpdate } from '../lib/version';
import { watchKeyboard } from '../lib/keyboard';
import { PreviewView } from './PreviewView';
import { fold } from '../lib/search';
import { isMangled, openBytes, openLabel, openLegacyLabel, sealBytes, sealLabel } from '../lib/format';
import { squareJpeg } from '../lib/avatar';
import { exportChat, type ExportProgress } from '../lib/exportchat';
import { RecoveryCode } from './RecoveryCode';
import { createAccountKey, unlock, devIdentity } from '../lib/keys';
import { webauthn, idp, newAccountKey, type Credential } from '@wa/auth';
import { loadManifest, loadMessages, mimeFor, rememberManifest, allMembersOf } from '../lib/archive';
import { likelySameChat } from '../lib/names';
import { canInstall, install, dismiss as dontAsk, onInstallable } from '../lib/install';
import { isNativeSignIn, nativeSignInToStart, handBackToApp, handBackWithError } from '../lib/native';
import { isAppOrigin, goToApp, SHOP } from '../lib/origin';
import { rememberedConfig, keepConfig } from '../lib/authcache';
import { whatCameBack, mark, noteUser } from '../lib/diagnose';
import type { ParsedMessage } from '../lib/parser';
import { GoogleMark, AppleMark } from './BrandMarks';
import { ChatView } from './ChatView';
import { Importer } from './Importer';

const b64 = (b: Uint8Array) => btoa(String.fromCharCode(...b));
const fromB64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));


export function App() {
  const [account, setAccount] = useState<Account | null>(null);
  const [key, setKey] = useState<Uint8Array | null>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [active, setActive] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<ParsedMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Começa pela última resposta conhecida, para os botões existirem já no
  // primeiro quadro em vez de aparecerem quando a rede volta.
  const [cfg, setCfg] = useState<AuthConfig | null>(rememberedConfig);
  /**
   * Se o que temos veio do servidor ou é a cópia guardada.
   *
   * Pintar um botão a partir da cópia é seguro. Partir para o provedor a
   * partir dela não é: o arranque automático dispara ao ver `cfg`, e com a
   * cópia isso acontece no primeiro render — cedo demais, e o
   * redirecionamento não voltava. O desenho usa `cfg`; a decisão de
   * entrar espera por isto.
   */
  const [configIsFresh, setConfigIsFresh] = useState(false);
  /** Qual coluna a barra lateral está mostrando — a trilha à esquerda troca. */
  const [panel, setPanel] = useState<Panel>('chats');
  /* A conversa com a IA vive aqui e não dentro do painel: trocar de aba
     desmontava o painel e o fio sumia. Nada é gravado — fechar a janela
     continua encerrando a conversa. */
  const [askThread, setAskThread] = useState<Thread>({ said: [], turns: [] });
  const [showHowTo, setShowHowTo] = useState(false);
  /**
   * O convite para instalar, que no Android é o que põe o OkaZip na folha de
   * compartilhamento do WhatsApp. Só existe quando o Chrome diz que dá — em
   * iPhone nenhum, e em nenhum aparelho onde já está instalado.
   */
  const [installable, setInstallable] = useState(canInstall());
  useEffect(() => onInstallable(() => setInstallable(canInstall())), []);
  /** An import whose conversation has nearly, but not exactly, the name of
   *  one already here. The promise is the importer, waiting. */
  const [joinAsk, setJoinAsk] = useState<
    { incoming: string; existing: string; resolve: (join: boolean) => void } | null>(null);
  const [showSignIn, setShowSignIn] = useState(false);
  // A search hit to land on. Consumed by ChatView, then cleared.
  const [focusSeq, setFocusSeq] = useState<number | undefined>(undefined);
  /**
   * Uma conversa com a IA por conversa do arquivo, e mais a global.
   *
   * Perguntar sobre o grupo das obras e depois sobre a Ana são dois
   * assuntos, não um; partilhar um fio entre eles fazia a segunda pergunta
   * arrastar o contexto da primeira. A chave é o id da conversa; a global
   * vive à parte, no `askThread`.
   */
  const [chatAsk, setChatAsk] = useState<Record<string, Thread>>({});
  /** O painel da direita, aberto a partir do cabeçalho da conversa. */
  const [sideAsk, setSideAsk] = useState(false);
  /** Em que secção a conta abre, para quem chega lá à procura de uma. */
  const [profileAt, setProfileAt] = useState<'menu' | 'ai'>('menu');

  /**
   * Quem fala nesta conversa, na ordem em que apareceram.
   *
   * O WhatsApp escreve isto sob o nome do grupo, e é a informação que faltava
   * para saber se a conversa aberta é a certa antes de ler qualquer mensagem.
   * Não vem de lugar nenhum guardado: são os remetentes do próprio arquivo,
   * então aparece conforme a conversa é decifrada e some quando se fecha.
   */
  const roster = useMemo(() => {
    if (!messages) return '';
    const seen: string[] = [];
    for (const m of messages) {
      if (m.sender && !seen.includes(m.sender)) seen.push(m.sender);
      if (seen.length > 24) break;
    }
    return seen.join(', ');
  }, [messages]);
  // Reading an export with no account and no upload — see the gate.
  const [preview, setPreview] = useState<Preview | null>(null);
  const [pendingImport, setPendingImport] = useState<File | null>(null);
  // Um export que chegou pela folha de compartilhamento do Android, antes de
  // sabermos se há sessão para recebê-lo.
  const [shared, setShared] = useState<File | null>(null);
  const [restoring, setRestoring] = useState(() => hasSession());
  const [staleBuild, setStaleBuild] = useState(false);
  const [importing, setImporting] = useState(false);
  // Google proves the account; it cannot unlock it. When an account has no key
  // credential yet, or none this device can use, we hold the session here and
  // ask for the missing half rather than showing an archive that will not open.
  const [pendingKey, setPendingKey] = useState<{ credentials: Credential[]; isNew: boolean } | null>(null);
  // Shown exactly once, after registration. Nothing continues until the person
  // confirms they have written it down, because there is no second chance.
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [recoveryEntry, setRecoveryEntry] = useState<string | null>(null);
  /** Which of the two archives someone is signing up for. Defaulted rather than
   *  blank: a required choice, made before anyone knows what the product is,
   *  costs more sign-ups than it buys understanding. */
  /** Set right after a Google sign-up, and cleared when the choice is made. */
  /** The landing page's menu. Every way in that is not the main one lives
   *  behind it, so the first screen has one thing to do. */
  const [menuOpen, setMenuOpen] = useState(false);
  const swReady = useRef(false);
  // O "+" do cabeçalho aciona o seletor que vive dentro do Importer.
  const pickFile = useRef<(() => void) | null>(null);

  /**
   * Deployed fixes do not reach an open tab, and on a phone a tab can outlive
   * several of them — one person spent an hour on a bug already fixed, with no
   * way to know. Pinned rather than placed in a layout, so it shows on every
   * screen including the gate, which is where someone stuck on an old build is
   * most likely to be standing.
   *
   * Offered, never forced, and never during an import: reloading would throw
   * the upload away.
   */
  const banner = staleBuild && !importing ? (
    <div className="update">
      <span>A newer version of OkaZip is available.</span>
      <button onClick={() => location.reload()}>Reload</button>
    </div>
  ) : null;
  // O campo da barra lateral filtra conversas pelo nome. Buscar *mensagem*
  // mudou de lugar: mora no cabeçalho da conversa aberta, que é onde a
  // pergunta "onde foi que ele disse aquilo" é feita.
  const [chatQuery, setChatQuery] = useState('');

  /**
   * Titles are sealed with the account key, so reading one is asynchronous and
   * cannot happen during render. They are opened once per list into here.
   *
   * Rows written before titles were sealed hold `btoa(title)` — plaintext the
   * server could read. `openLabel` returns null for those, and they are
   * re-sealed in place on sight rather than left for a migration nobody runs.
   */
  /** The list as it is now, for callbacks that outlive the render that made
   *  them — the importer holds one for the length of an import. */
  const chatsRef = useRef<Chat[]>([]);
  chatsRef.current = chats;

  const [titles, setTitles] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!key || chats.length === 0) return;
    let live = true;
    void (async () => {
      const out: Record<string, string> = {};
      const legacy: Array<{ chatId: string; title: string }> = [];
      for (const c of chats) {
        const opened = await openLabel(key, c.titleCiphertext);
        if (opened !== null) { out[c.chatId] = opened; continue; }
        const plain = openLegacyLabel(c.titleCiphertext) ?? 'Conversation';
        out[c.chatId] = plain;
        // Never seal a name that came back broken. This migration used to
        // decode Latin-1 bytes as UTF-8 and then write the result back, which
        // turned a one-time display fault into a permanent one: the original
        // was overwritten by the mangled version. Leaving the old value in
        // place keeps the bytes recoverable.
        if (!isMangled(plain)) legacy.push({ chatId: c.chatId, title: plain });
      }
      if (!live) return;
      setTitles(out);
      for (const l of legacy) {
        await api.renameChat(l.chatId, await sealLabel(key, l.title)).catch(() => undefined);
      }
    })();
    return () => { live = false; };
  }, [chats, key]);

  const titleOf = useCallback(
    (c: Chat) => titles[c.chatId] ?? '', [titles]);

  /**
   * Chat pictures, opened into object URLs once. Revoked when the list
   * changes, because a blob URL outlives the render that made it and a chat
   * list that is refreshed all day would otherwise leak one per refresh.
   */
  const [avatars, setAvatars] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!key) return;
    let live = true;
    const made: string[] = [];
    void (async () => {
      const out: Record<string, string> = {};
      for (const c of chats) {
        if (!c.avatarCiphertext) continue;
        const bytes = await openBytes(key, c.avatarCiphertext);
        if (!bytes) continue;
        const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'image/jpeg' }));
        made.push(url);
        out[c.chatId] = url;
      }
      if (live) setAvatars(out); else made.forEach(URL.revokeObjectURL);
    })();
    return () => { live = false; made.forEach(URL.revokeObjectURL); };
  }, [chats, key]);

  const avatarOf = useCallback(
    (c: Chat) => avatars[c.chatId], [avatars]);

  /**
   * Leaving has to be possible, so the archive gives a conversation back in
   * the shape it arrived in — a zip our own importer can read.
   *
   * Media comes through the Service Worker, the same URL an <img> uses, which
   * means the bytes are decrypted on the way out exactly as they are on the
   * way to the screen and nothing here learns a key.
   */
  const [exporting, setExporting] = useState<ExportProgress | null>(null);
  /** Which conversation, so a row can say "Exporting…" on itself alone. */
  const [exportingId, setExportingId] = useState<string | null>(null);
  const doExport = useCallback(async (chat: Chat, msgs: ParsedMessage[]) => {
    setExporting({ done: 0, total: 0 });
    setExportingId(chat.chatId);
    try {
      const { blob, filename, missing } = await exportChat(
        titles[chat.chatId] ?? 'Conversation', msgs,
        async (attach) => {
          const r = await fetch(`/media/${attach}`);
          if (!r.ok) return null;
          return { blob: await r.blob(), mime: r.headers.get('content-type') ?? '' };
        },
        setExporting);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.append(a); a.click(); a.remove();
      // Revoked late: Safari cancels the download if the URL dies too soon.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (missing) setError(`Exported. ${missing} attachment${missing === 1 ? '' : 's'} could not be read and are marked as omitted in the transcript.`);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally { setExporting(null); setExportingId(null); }
  }, [titles]);

  /**
   * A conversation imported before the archive recorded what it refers to
   * registers itself the first time it is opened — the one moment the client
   * has every chunk decrypted and therefore knows. Cheap, idempotent, and
   * quiet: nothing on screen depends on it.
   */
  const registered = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!key || !active?.directoryCid || !messages) return;
    const id = active.chatId;
    if (registered.current.has(id)) return;
    registered.current.add(id);
    void allMembersOf(key, active.directoryCid)
      .then((ids) => api.registerChatMembers(id, ids))
      .catch(() => registered.current.delete(id));
  }, [active?.chatId, messages, key]);

  /**
   * Removing one conversation, and everything that came with it.
   */
  /**
   * Exportar a partir da lista.
   *
   * O cabeçalho exportava o que já estava na tela. Da lista, a conversa pode
   * nunca ter sido aberta — então os trechos são buscados e decifrados aqui,
   * que é o mesmo trabalho que abrir faria, sem abrir.
   */
  const exportFromList = useCallback(async (chat: Chat) => {
    if (!key || !chat.directoryCid) return;
    if (active?.chatId === chat.chatId && messages) return void doExport(chat, messages);
    setExporting({ done: 0, total: 0 });
    setExportingId(chat.chatId);
    try {
      const msgs = await loadMessages(key, chat.directoryCid);
      await doExport(chat, msgs);
    } catch (e) {
      setError(String((e as Error).message ?? e));
      setExporting(null); setExportingId(null);
    }
  }, [key, active, messages, doExport]);

  /**
   * A conversa que se pediu para remover, à espera de confirmação.
   *
   * Era `window.confirm`, que num WKWebView não existe: sem um
   * `WKUIDelegate` a app não desenha caixa nenhuma e a função devolve
   * `false` na hora — o botão simplesmente não fazia nada no iPhone, e
   * fazia tudo no site. Perguntar dentro da própria app é o que a
   * eliminação de conta já fazia, e funciona em qualquer lado.
   */
  const [removing, setRemoving] = useState<Chat | null>(null);

  const removeChat = useCallback(async (chat: Chat) => {
    if (!key) return;
    setRemoving(null);
    try {
      const r = await api.deleteChat(chat.chatId);
      setActive(null); setMessages(null);
      await refresh();
      if (r.spacePending) {
        setError('Removed. The space it used will come back once every other conversation has been opened once on this device — until then the archive cannot tell what belongs to which.');
      } else if (r.freedBytes) {
        setError(`Removed, and ${(r.freedBytes / 1024 ** 2).toFixed(1)} MB came back.`);
      }
    } catch (e) { setError(String((e as Error).message ?? e)); }
    // `refresh` is declared below and its own deps are empty; see setAvatar.
  }, [key, titles]);

  const setAvatar = useCallback(async (chat: Chat, file: File | null) => {
    if (!key) return;
    try {
      if (!file) {
        await api.setChatAvatar(chat.chatId, null);
      } else {
        const small = await squareJpeg(file);
        await api.setChatAvatar(chat.chatId, await sealBytes(key, small));
      }
      await refresh();
    } catch (e) { setError(String((e as Error).message ?? e)); }
    // `refresh` is declared further down and a dependency array is evaluated
    // during render, so naming it here would throw before the component ever
    // mounted. Its own deps are empty, so the closure cannot go stale.
  }, [key]);

  const rename = useCallback(async (chat: Chat, name: string) => {
    if (!key) return;
    const clean = name.trim();
    if (!clean || clean === titles[chat.chatId]) return;
    setTitles((t) => ({ ...t, [chat.chatId]: clean }));
    try { await api.renameChat(chat.chatId, await sealLabel(key, clean)); }
    catch (e) { setError(String((e as Error).message ?? e)); await refresh(); }
  }, [key, titles]);

  const shownChats = useMemo(() => {
    const needle = fold(chatQuery.trim());
    if (!needle) return chats;
    return chats.filter((c) => fold(titles[c.chatId] ?? '').includes(needle));
  }, [chats, chatQuery, titles]);

  // The Service Worker holds the key so <img src="/media/…"> can work. It can
  // be killed between requests, so it asks for the key again when it needs to.
  const primeServiceWorker = useCallback(async (accountKey: Uint8Array) => {
    const reg = await navigator.serviceWorker.ready;
    const manifest = loadManifest();
    reg.active?.postMessage({ type: 'init', accountKey, token: api.currentToken(), members: manifest });
    if (!swReady.current) {
      navigator.serviceWorker.addEventListener('message', (e) => {
        if ((e.data as { type?: string })?.type === 'need-init')
          reg.active?.postMessage({ type: 'init', accountKey, token: api.currentToken(), members: loadManifest() });
      });
      swReady.current = true;
    }
  }, []);

  const signIn = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      // Sign in first and see what the account already holds. Minting a key
      // unconditionally is what stranded every previously imported chat.
      const identity = devIdentity();
      let r = await api.signIn(identity, []);
      let credentials = r.account.credentials ?? [];

      if (credentials.length === 0) {
        const { credential } = await createAccountKey();
        r = await api.signIn(identity, [credential]);
        credentials = r.account.credentials ?? [credential];
      }

      const accountKey = await unlock(credentials);
      setAccount(r.account); setKey(accountKey);
      await primeServiceWorker(accountKey);
      setChats((await api.chats()).chats);
    } catch (e) {
      setError(e instanceof ApiError ? e.problem.title : String(e));
    } finally { setBusy(false); }
  }, [primeServiceWorker]);

  useEffect(() => {
    // Um `null` aqui apagava o portão inteiro: sem configuração, nenhuma porta
    // é desenhada e a pessoa fica olhando uma página sem botão. Se o servidor
    // não responde, isso é uma configuração também — a que não oferece nada —
    // e a tela diz o motivo em vez de sumir.
    api.authConfig()
      .then((c) => { setCfg(c); keepConfig(c); })
      .finally(() => setConfigIsFresh(true))
      .catch(() => setCfg({
      passkeysAvailable: false, rpId: null, googleAvailable: false,
      escrowAvailable: false, firebase: null,
      reason: 'this deployment did not answer /v1/auth/config',
    }));
  }, []);

  useEffect(() => watchForUpdate(() => setStaleBuild(true)), []);
  useEffect(() => watchKeyboard(), []);

  /**
   * Come back to an open tab and still be here.
   *
   * Nothing used to survive a reload — and iOS discards tabs freely, so
   * "signed in" lasted until Safari felt like reclaiming memory. An account
   * whose key the service holds can be restored with no gesture at all:
   * refresh the session, ask for the key, carry on.
   *
   * An account that keeps its own key cannot be, and should not be: without
   * the authenticator there is nothing to unwrap with. That one gets the gate,
   * which is the whole point of holding your own key.
   */
  useEffect(() => {
    if (!hasSession() || account) { setRestoring(false); return; }
    let cancelled = false;
    void (async () => {
      try {
        if (!(await api.freshToken())) return;
        const acct = await api.account();
        if (!(acct.credentials ?? []).some((c) => c.method === 'escrow')) return;
        const { accountKey } = await api.heldKey();
        if (!cancelled) await enter(acct, fromB64(accountKey));
      } catch { /* fall through to the gate, which always works */ }
      finally { if (!cancelled) setRestoring(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * "Export chat" in WhatsApp, then OkaZip in the share sheet: the zip is
   * already here, posted to the Service Worker and left in the inbox. Taking
   * it is the first thing this tab does, and the query string is cleaned
   * immediately so a reload does not look like a second delivery.
   */
  useEffect(() => {
    const how = new URLSearchParams(location.search).get('shared');
    if (!how) return;
    history.replaceState(null, '', location.pathname);
    if (how !== '1') {
      setError('That file did not come through. Try Export chat again, or use +.');
      return;
    }
    void takeSharedFile()
      .then((f) => { if (f) setShared(f); })
      .catch(() => setError('That file did not come through. Try Export chat again, or use +.'));
  }, []);

  /**
   * Where it goes depends on whether there is an account yet, which is not
   * known at arrival — a held-key session restores itself a moment later. So
   * it waits for that to settle, then takes the same two paths everyone else
   * does: straight to an import, or to a preview that asks for an account
   * afterwards.
   */
  useEffect(() => {
    if (!shared || restoring) return;
    if (account && key) setPendingImport(shared);
    else void tryLocally(shared);
    setShared(null);
  }, [shared, restoring, account, key]);

  /** Everything that happens once an account and its key are both in hand. */
  const enter = useCallback(async (acct: Account, accountKey: Uint8Array) => {
    setAccount(acct);
    setKey(accountKey);
    await primeServiceWorker(accountKey);
    setChats((await api.chats()).chats);
  }, [primeServiceWorker]);

  const adopt = useCallback(async (r: {
    accessToken: string; refreshToken: string;
    account: { accountId: string; homeRegion: string; status: string; quota: never; credentials?: Credential[] };
    accountKey: Uint8Array;
  }) => {
    api.adoptSession(r.accessToken, r.refreshToken);
    await enter(r.account as unknown as Account, r.accountKey);
    // Carried over from a preview: they already chose the file, so do not make
    // them find it again.
    if (preview) { setPendingImport(preview.file); preview.dispose(); setPreview(null); }
  }, [primeServiceWorker, preview]);

  /**
   * Google sign-in. It establishes the session and nothing else — the archive
   * stays shut until a passkey on this device can produce the key.
   */
  /**
   * Everything after Google has said who this is.
   *
   * Split out because the token arrives two ways. In a browser it comes back
   * from a popup and this runs in the same breath. In an app launched from
   * the home screen there is no browser to put a popup in, so the page goes
   * to Google and comes back new — and this has to run again, on load,
   * against a token found rather than awaited.
   */
  const finishGoogle = useCallback(async (idToken: string) => {
    if (!cfg?.firebase) return;
    setBusy(true); setError(null);
    try {
      const r = await api.signInWithIdp(idToken);
      api.adoptSession(r.accessToken, r.refreshToken);

      const held = (r.account.credentials ?? []).some((c) => c.method === 'escrow');

      if (r.needsKeyCredential) {
        // A new account. If the service will hold the key, signing up is over
        // here — no passkey, nothing to write down, nothing to lose. Offering a
        // passkey belongs in the profile, once they know what the product is.
        if (cfg.escrowAvailable) {
          // Signing up ends here. The key is held, the account opens, and
          // there is nothing to write down or lose.
          //
          // There used to be a screen in between asking who should keep the
          // key. It was built because a choice nobody notices is a default
          // with an alibi, and that reasoning still stands — but it asked
          // the hardest question in the product of somebody who had not yet
          // seen a single message. The choice now lives in the account
          // panel, next to a sentence that says plainly that we can read
          // this archive, which is the same disclosure a beat later.
          const accountKey = newAccountKey();
          await api.holdKey(b64(accountKey));
          await adopt({ ...r, accountKey } as never);
          return;
        }
        const reg = await webauthn.registerPasskey((path: string, body: unknown) => api.post(path, body));
        await adopt(reg as never);
        if (reg.recoveryWords) setRecovery(reg.recoveryWords);
        return;
      }

      // Returning. The held key belongs to the account that just signed in, so
      // it is used first.
      //
      // A passkey assertion is NOT a way to unlock this account: it resolves to
      // whichever account that credential belongs to, which need not be this
      // one. Doing it here silently moved someone from the Google account they
      // had just chosen into a different archive on a laptop that happened to
      // hold another passkey — the same person saw two different sets of chats
      // on two devices. If a passkey is used at all, its account has to be
      // checked against this one.
      if (held) {
        const { accountKey } = await api.heldKey();
        await adopt({ ...r, accountKey: fromB64(accountKey) } as never);
        return;
      }

      try {
        const viaPasskey = await webauthn.loginWithPasskey(
          (path: string, body: unknown) => api.post(path, body));
        if (viaPasskey.account.accountId === r.account.accountId) {
          await adopt(viaPasskey as never);
          return;
        }
        // A credential for someone else's archive. Put the Google session back
        // and ask for this account's key rather than opening the wrong one.
        api.adoptSession(r.accessToken, r.refreshToken);
      } catch { /* no passkey here */ }

      setPendingKey({ credentials: r.account.credentials ?? [], isNew: false });
    } catch (e) {
      setError(idp.explain(e));
    } finally { setBusy(false); }
  }, [cfg, adopt]);

  const continueWith = useCallback(async (which: idp.Provider) => {
    if (!cfg?.firebase) return;
    // O Google, dentro do app, pertence ao Safari: é lá que vive a passkey
    // okazip.com explica o produto; quem entra, entra em app.okazip.com.
    // Uma porta só, num endereço só — é o que a casca carrega, o que o
    // Android abre e o que fica na tela de início, e é lá que a sessão
    // tem de nascer para servir os três.
    //
    // Três exceções. A casca do iOS está em pé no lugar do app e a sessão
    // tem de nascer na origem que ela carrega. A folha de autenticação que
    // ela abre é a mesma coisa vista de dentro. E uma pré-visualização em
    // curso segura um ficheiro que não atravessa origens: mandá-la embora
    // perdia o arquivo que a pessoa acabou de abrir.
    if (!isAppOrigin() && !isNativeSignIn() && !idp.inNativeShell() && !preview) {
      goToApp();
      return;
    }
    // Dentro da casca, os dois provedores vão pelo mecanismo do sistema:
    // a Google por PKCE numa folha de autenticação, a Apple pela folha do
    // sistema com Face ID. A casca devolve o token e a página troca-o por
    // uma sessão — ver okazipGoogle e okazipApple acima.
    if (idp.inNativeShell()) {
      const bridge = (window as { webkit?: { messageHandlers?:
        { okazip?: { postMessage: (m: unknown) => void } } } }).webkit;
      if (bridge?.messageHandlers?.okazip) {
        bridge.messageHandlers.okazip.postMessage({ signIn: which });
        return;
      }
    }
    setBusy(true); setError(null);
    try {
      // Null means the page is on its way to Google and will come back
      // through the effect below, not through here.
      // Sem popup quando esta janela é a folha aberta pelo app.
      mark(`start:${which}${isNativeSignIn() ? ' redirect' : ' popup'}`);
      const idToken = await idp.signIn(cfg.firebase, which, 'signin', isNativeSignIn());
      if (idToken) await finishGoogle(idToken);
    } catch (e) {
      const why = idp.explain(e) ?? 'cancelled';
      // Em pé no lugar do app, um erro desenhado aqui fica preso na folha.
      if (!handBackWithError(why)) setError(why);
    } finally { setBusy(false); }
  }, [cfg, finishGoogle, preview]);

  /** Coming back from Google's own page, in an app with no browser around it. */
  const cameBack = useRef(false);
  useEffect(() => {
    const intent = idp.returningFromIdp();
    mark(`load${location.search ? '?' + location.search.slice(1, 40) : ''}${intent ? ' back:' + intent : ''}`);
    if (!cfg?.firebase || !intent || cameBack.current) return;
    // Uma vez só. `cfg` troca de identidade quando a resposta da rede
    // substitui a cópia guardada, e a limpeza do efeito cancelava o
    // resultado que vinha a caminho — o login acontecia e era deitado fora.
    cameBack.current = true;
    let live = true;
    setBusy(true);
    void idp.tokenAfterRedirect(cfg.firebase)
      .then(async (idToken) => {
        if (!live) return;
        // Voltar da Google sem token nenhum é um beco sem saída silencioso:
        // a folha fica na página e ninguém sabe que acabou.
        if (!idToken) {
          // Distingue "ninguém entrou" de "entrou e o resultado perdeu-se".
          noteUser(await idp.whoIsSignedIn(cfg.firebase!).catch(() => null));
          handBackWithError(`no token — ${whatCameBack()}`);
          return;
        }
        // Linking says "this Google account also opens the archive I am in".
        // Handing that token to sign-in instead would open whichever archive
        // the account belongs to, which is the wrong one by definition.
        if (intent === 'link') { await api.linkIdentity(idToken); await refresh(); return; }
        await finishGoogle(idToken);
      })
      .catch((e) => {
        if (!live) return;
        const why = idp.explain(e) ?? 'cancelled';
        if (!handBackWithError(why)) setError(why);
      })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [cfg, finishGoogle]);

  const addThisDevice = useCallback(async (words: string[]) => {
    if (!pendingKey) return;
    setBusy(true); setError(null);
    try {
      const r = await webauthn.addThisDevice(
        (path: string, body: unknown) => api.post(path, body), pendingKey.credentials, words);
      setPendingKey(null);
      await adopt(r as never);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally { setBusy(false); }
  }, [pendingKey, adopt]);

  const createArchive = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const r = await webauthn.registerPasskey((path: string, body: unknown) => api.post(path, body));
      await adopt(r as never);
      if (r.recoveryWords) setRecovery(r.recoveryWords);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally { setBusy(false); }
  }, [adopt]);

  const signInWithPasskey = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      await adopt(await webauthn.loginWithPasskey((path: string, body: unknown) => api.post(path, body)) as never);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally { setBusy(false); }
  }, [adopt]);

  /**
   * The lost-device path. A passkey assertion still has to happen first — it is
   * what tells the server which account this is — and the recovery code then
   * unwraps the key that this device's passkey cannot.
   */
  const tryLocally = useCallback(async (file: File) => {
    setError(null);
    try {
      preview?.dispose();
      setPreview(await openPreview(file));
    } catch (e) {
      setError(String((e as Error).message ?? e));
    }
  }, [preview]);

  const recoverWithCode = useCallback(async () => {
    const words = (recoveryEntry ?? '').trim().split(/\s+/);
    setBusy(true); setError(null);
    try {
      const r = await webauthn.loginWithPasskey((path: string, body: unknown) => api.post(path, body));
      const accountKey = await webauthn.unlockWithRecoveryCode(r.account.credentials ?? [], words);
      setRecoveryEntry(null);
      await adopt({ ...r, accountKey } as never);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally { setBusy(false); }
  }, [recoveryEntry, adopt]);

  const refresh = useCallback(async () => {
    setAccount(await api.account());
    setChats((await api.chats()).chats);
  }, []);

  /**
   * Em pé no lugar do app: assim que há sessão, ela volta para ele e esta
   * janela fecha. Nada é desenhado — a pessoa nem vê que passou por aqui.
   */
  useEffect(() => {
    if (account && isNativeSignIn()) handBackToApp();
  }, [account]);

  /**
   * A porta que a casca do iOS usa para entregar um token da Google.
   *
   * Ela obtém-no nativamente — o mecanismo do sistema, onde a passkey da
   * Google está — e chama isto. Nada viaja numa URL: o token entra por
   * aqui e sai daqui uma sessão, pelo mesmo caminho de sempre.
   */
  useEffect(() => {
    if (!idp.inNativeShell() || !cfg?.firebase) return;
    const w = window as unknown as {
      okazipGoogle?: (t: string) => void;
      okazipApple?: (t: string, nonce: string, name: string | null) => void;
    };
    const firebase = cfg.firebase;
    w.okazipGoogle = (googleIdToken: string) => {
      setBusy(true); setError(null);
      void idp.signInWithGoogleIdToken(firebase, googleIdToken)
        .then(finishGoogle)
        .catch((e) => setError(idp.explain(e) ?? 'sign-in failed'))
        .finally(() => setBusy(false));
    };
    w.okazipApple = (idToken: string, rawNonce: string) => {
      setBusy(true); setError(null);
      void idp.signInWithAppleIdToken(firebase, idToken, rawNonce)
        .then(finishGoogle)
        .catch((e) => setError(idp.explain(e) ?? 'sign-in failed'))
        .finally(() => setBusy(false));
    };
    return () => { delete w.okazipGoogle; delete w.okazipApple; };
  }, [cfg, finishGoogle]);

  /**
   * Uma folha que não avança fecha-se a dizer porquê.
   *
   * Em pé no lugar do app, esta página é de passagem: arranca o login e
   * sai, ou volta do provedor e entrega a sessão. Ficar aqui não é um
   * estado — é uma avaria, e até agora a única saída era a pessoa fechar
   * a folha, o que chega ao app como "cancelado" e perde o motivo.
   */
  useEffect(() => {
    if (!isNativeSignIn() || account) return;
    const t = setTimeout(() => {
      if (!hasSession()) handBackWithError(`stalled — ${whatCameBack()}`);
    }, 12_000);
    return () => clearTimeout(t);
  }, [account]);

  /** E chega já entrando, em vez de mostrar uma landing com um botão. */
  useEffect(() => {
    if (!configIsFresh || !cfg?.firebase || account) return;
    // Não arrancar por cima de um regresso: a página que volta do provedor
    // tem de o processar, não partir outra vez. É este o guarda contra o
    // ciclo — ver nativeSignInToStart.
    if (idp.returningFromIdp()) return;
    const which = nativeSignInToStart();
    if (which) void continueWith(which);
  }, [configIsFresh, cfg, account, continueWith]);

  /**
   * What the archive already holds for a conversation the importer is about
   * to write.
   *
   * The importer runs in a worker, which has the key but neither the chat
   * list nor localStorage, so it cannot find or decrypt anything by itself.
   * It works out which conversation the file is and asks here; the answer is
   * merged with the file so a second export adds to the first instead of
   * standing in for it.
   *
   * Nothing is the right answer to every failure: a conversation that will
   * not decrypt is one written under another key, and refusing to import on
   * account of it would leave somebody with no way forward at all. The worst
   * case of answering "nothing" is the behaviour we had before.
   */
  const existingFor = useCallback(async (chatId: string, title: string) => {
    const nothing = { messages: [] as ParsedMessage[] };
    if (!key) return nothing;
    // Asked of the server, not of this component. The importer announces
    // "done" before the refresh that follows it has returned, so a second
    // file chosen straight after the first runs against a list that does not
    // yet contain the conversation the first one wrote — and an empty list
    // matches nothing, which is indistinguishable from "this is new". The
    // ref is the fallback for when the network is the thing that failed.
    const held = await api.chats().then((r) => r.chats).catch(() => chatsRef.current);
    const read = async (c: Chat) => {
      if (!c.directoryCid) return [] as ParsedMessage[];
      try { return await loadMessages(key, c.directoryCid); } catch { return []; }
    };

    // The name in the file is the name the archive knows it by: same chat.
    const exact = held.find((c) => c.chatId === chatId);
    if (exact) return { messages: await read(exact) };

    // Decrypted here rather than read from `titles`, which is filled by an
    // effect: on a phone the import can get this far before that effect has
    // run, and a list of blank names matches nothing.
    const named = await Promise.all(held.map(async (chat) => ({
      chatId: chat.chatId, chat,
      title: titleOf(chat)
        || (await openLabel(key, chat.titleCiphertext).catch(() => null))
        || openLegacyLabel(chat.titleCiphertext) || '',
    })));

    // Close, but not the same. A group renamed between two exports hashes to
    // two ids, and taking that for a new conversation is how an archive ends
    // up with the same chat twice, each holding half of it. Nothing here can
    // tell a rename from a second group with a similar name, though — so it
    // is put to the person rather than guessed at.
    const near = likelySameChat(title, named);
    if (!near || !near.title) return nothing;
    if (!(await new Promise<boolean>((resolve) =>
      setJoinAsk({ incoming: title, existing: near.title, resolve })))) return nothing;
    return { messages: await read(near.chat), chatId: near.chatId, title: near.title };
  }, [key, titleOf]);

  useEffect(() => {
    if (!active?.directoryCid || !key) { setMessages(null); return; }
    let cancelled = false;
    setMessages(null); setError(null);
    loadMessages(key, active.directoryCid,
      // The end of the conversation, drawn while the history is still coming.
      (partial) => { if (!cancelled) setMessages(partial); })
      .then((m) => { if (!cancelled) setMessages(m); })
      .catch((e) => {
        if (cancelled) return;
        // A decryption failure means this chat was written under a different
        // key. Saying so beats sitting on "Decrypting…" indefinitely.
        const raw = String(e?.message ?? e);
        setError(/OperationError|operation-specific/i.test(raw)
          ? 'This chat cannot be decrypted with the key on this device. It was imported under a different account key.'
          : raw);
      });
    return () => { cancelled = true; };
  }, [active, key]);

  if (recovery) {
    return (
      <RecoveryCode words={recovery} googleAvailable={cfg?.googleAvailable === true}
                    onConfirmed={() => setRecovery(null)} />
    );
  }

  // Signed in, and the archive still shut: this device has no passkey that can
  // open it. One recovery code, once, and then the passkey takes over.
  if (pendingKey) {
    return (
      <div className="gate">
        <h1>Add this device</h1>
        <p>Your archive is on this account, but this device has no key for it yet.
           Enter your recovery code once and we will register a passkey here —
           after that, this device opens it on its own.</p>
        <div className="recover">
          <label htmlFor="add-words">Your twenty-four words</label>
          <textarea id="add-words" rows={3} value={recoveryEntry ?? ''}
                    onChange={(e) => setRecoveryEntry(e.target.value)} />
          <button disabled={busy || !recoveryEntry?.trim()}
                  onClick={() => addThisDevice((recoveryEntry ?? '').trim().split(/\s+/))}>
            {busy ? 'Adding…' : 'Add this device'}
          </button>
        </div>
        {error && <p className="err">{error}</p>}
      </div>
    );
  }

  // Before the gate on purpose. A person reading their own export has not been
  // asked for anything yet, and the whole point is that they are not.
  if (preview && (!account || !key)) {
    return (
      <>
        {banner}
        <PreviewView preview={preview} busy={busy}
                   onKeep={() => { void continueWith('google'); }}
                   onPasskey={cfg?.passkeysAvailable
                     ? () => { void signInWithPasskey(); } : undefined}
                   onDiscard={() => { preview.dispose(); setPreview(null); }} />
      </>
    );
  }

  if (!account || !key) {
    const canPasskey = cfg?.passkeysAvailable === true;
    // Not a secret, just unadvertised: making an account without Google is a
    // normal thing the product can do, and the e2e suites are the only party
    // that needs it on a stack where Google works for people and not for
    // robots.
    const wantsPasskey = new URLSearchParams(location.search).has('passkey');
    // A refresh token good for thirty days is still here; what is missing is a
    // key, and only an authenticator can supply that.
    const returning = hasSession() && !restoring;
    const canGoogle = cfg?.googleAvailable === true && !!cfg?.firebase;
    // Escondido até o console estar pronto. A App Store recusa um app cuja
    // única porta é um login de terceiro (4.8), então isto existe primeiro
    // por obrigação — e depois porque nem todo mundo tem conta Google.
    const canApple = cfg?.appleAvailable === true && !!cfg?.firebase;
    // Google is the way in. The other two are for a stack without it, and for
    // the suites — see the footer below.
    const otherWays = canPasskey && (!canGoogle || wantsPasskey);
    // Com uma porta só, "Sign in" ia direto. Com duas há o que escolher, e
    // a folha volta a ter função.
    const aChoice = canApple || otherWays;
    // Na landing não se entra: encaminha-se. Mesma regra do `continueWith`,
    // precisa de estar aqui também porque este botão abre uma folha de
    // escolha em vez de chamar o login — e escolher entre portas é coisa
    // do endereço onde as portas estão.
    const elsewhere = !isAppOrigin() && !isNativeSignIn() && !idp.inNativeShell();
    // O endereço do app abre numa caixa só, centrada. Ver lib/origin.
    const appGate = !returning && isAppOrigin();
    // "Sign in" na caixa de entrada, onde é a única coisa que ali acontece;
    // "Continue" para quem volta, que já tem sessão e só a está a retomar.
    const verb = appGate ? 'Sign in with' : 'Continue with';
    // As portas, escritas uma vez. Quem volta e quem chega pelo endereço do
    // app veem o mesmo conjunto.
    const ways = (
      <>
        <div className="actions">
          {canGoogle && (
            <button onClick={() => void continueWith('google')} disabled={busy}>
              {busy ? 'Waiting…' : <>{appGate && <GoogleMark />}{`${verb} Google`}</>}
            </button>
          )}
          {canApple && (
            <button className={canGoogle ? 'ghost' : ''}
                    onClick={() => void continueWith('apple')} disabled={busy}>
              {busy ? 'Waiting…' : <>{appGate && <AppleMark />}{`${verb} Apple`}</>}
            </button>
          )}
          {otherWays && (
            <button className={canGoogle || canApple ? 'ghost' : ''}
                    onClick={signInWithPasskey} disabled={busy}>
              {busy ? 'Unlocking…' : 'Use a passkey'}
            </button>
          )}
        </div>
        {otherWays && (
          <p className="fine">
            <button className="link" onClick={() => setRecoveryEntry('')}>
              Use a recovery code instead
            </button>
          </p>
        )}
      </>
    );
    return (
      <div className={`gate${appGate ? ' gate-app' : ''}`}>
        {banner}

        {/* Someone who already has an archive here is not shopping. Sending
            them to the pitch after every tab close is what "it logs me out all
            the time" feels like, even when the session is intact. */}
        {returning ? (
          <>
            <h1>Welcome back.</h1>
            <p className="lede">Your archive is here. Sign in the way you set it
               up and it opens where you left it.</p>
            {ways}
          </>
        ) : appGate ? (
          /* O endereço do app não vende nada: é o que o iPhone carrega, o que
             o Android abre e o que fica na tela de início. Quem chega aqui já
             decidiu — uma caixa, as portas, e mais nada. A explicação do
             produto fica em okazip.com, a um link de distância para quem
             caiu aqui sem saber o que é. */
          <>
            {/* A marca no canto, como em qualquer site: diz onde a pessoa
                está sem disputar espaço com o que ela veio fazer, e é o
                caminho de volta para a página que explica o produto. */}
            <a className="app-mark" href={SHOP}>
              <img src="/icon.svg" width="26" height="26" alt="" />
              OkaZip
            </a>
            <div className="signin-card">
              <h1>Sign In to Your Account</h1>
              {ways}
            </div>
          </>
        ) : (
          <>
            <header className="lp-nav">
              <a className="lp-mark" href="#top">
                <img src="/icon.svg" width="26" height="26" alt="" />
                OkaZip
              </a>
              <button className="lp-burger" aria-label="Menu" aria-expanded={menuOpen}
                      onClick={() => setMenuOpen((o) => !o)}>
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d={menuOpen ? 'M6 6l12 12M18 6L6 18' : 'M4 7h16M4 12h16M4 17h16'} />
                </svg>
              </button>
              <nav className={`lp-links${menuOpen ? ' open' : ''}`}>
                <a href="#how">How it works</a>
                <a href="/privacy">Privacy</a>
                {/* A folha existe para escolher; com o Google sozinho não há
                    escolha, e um modal de um botão é um clique cobrado por
                    nada. Ela volta quando há outras portas — sem Google, ou
                    quando a URL pede.

                    "Sign in" e "Get started" chamam a mesma coisa de
                    propósito: o Google entra ou cadastra conforme o caso, e
                    quem está voltando procura a palavra "Sign in" e estranha
                    a falta dela. */}
                {(canPasskey || canGoogle) && (
                  <button className="lp-signin" disabled={busy}
                          onClick={() => {
                            if (elsewhere) { goToApp(); return; }
                            if (aChoice) setShowSignIn(true);
                            else void continueWith('google');
                          }}>
                    Sign in
                  </button>
                )}
                {canGoogle && (
                  <button className="lp-cta" onClick={() => void continueWith('google')} disabled={busy}>
                    Get started
                  </button>
                )}
              </nav>
            </header>

            <section className="lp-hero" id="top">
              <h1>Every conversation you have had, kept.</h1>
              <p className="lede">WhatsApp hands you a folder nobody can read.
                 OkaZip turns it back into the conversation — messages, photos,
                 video and voice notes, in order, searchable, on every device
                 you own.</p>
              {(canGoogle || canApple) && (
                <div className="actions">
                  <button onClick={() => void continueWith(canGoogle ? 'google' : 'apple')}
                          disabled={busy}>
                    {busy ? 'Waiting…' : 'Get started'}
                  </button>
                  <a href="#how">See how it works →</a>
                </div>
              )}
              {canGoogle && canApple && (
                <p className="fine lp-other">
                  <button className="link" onClick={() => void continueWith('apple')}
                          disabled={busy}>or continue with Apple</button>
                </p>
              )}
              <p className="fine">Continues with {canGoogle ? 'Google' : 'Apple'} · 1 GB free, no card ·
                 you choose who can open the archive on the next screen.</p>
              <div className="lp-shot">
                <img src="/shot-chat.png" width="2560" height="1600"
                     alt="OkaZip showing a family group chat with photos" />
              </div>
            </section>

            {/* No customer logos to show, and inventing them is the oldest lie
                on a landing page. Facts about the product instead. */}
            <div className="lp-band">
              <span>1 GB free</span><span>No card</span>
              <span>Export back out any time</span><span>Works offline once loaded</span>
            </div>

            <section className="lp-path" id="how">
              <p className="lp-eyebrow">How it works</p>
              <h2>Three steps, once.</h2>
              <ol>
                <li>
                  <strong>Export the chat</strong>
                  <span>In WhatsApp: the conversation, then ⋮ → More → Export
                        chat → Include media.</span>
                </li>
                <li>
                  <strong>Send it to OkaZip</strong>
                  <span>Pick OkaZip from the share sheet, or drop the zip
                        onto this page on a computer.</span>
                </li>
                <li>
                  <strong>Read it, for good</strong>
                  <span>It opens like the app you exported it from, on your
                        phone and your laptop, for as long as you keep it.</span>
                </li>
              </ol>
            </section>

            <section className="lp-feature">
              <div className="lp-feature-words">
                <p className="lp-eyebrow">Search</p>
                <h2>Find the thing you half remember.</h2>
                <p>Search every conversation by word, accents and all — "reuniao"
                   finds "reunião". Jump straight to the message, in context,
                   with the photo that came with it.</p>
                <p className="fine">Searching happens on your device, over text
                   the server never sees in the clear.</p>
              </div>
              <div className="lp-feature-shot">
                <img src="/shot-search.png" width="2560" height="1600" loading="lazy"
                     alt="Searching inside a conversation in OkaZip" />
              </div>
            </section>

            <section className="lp-pillars">
              <div>
                <strong>Nothing is left behind</strong>
                <span>Photos, video, voice notes and documents come across with
                      the messages they belonged to — not as a folder of files
                      with numbers for names.</span>
              </div>
              <div>
                <strong>Yours to take back</strong>
                <span>Export any conversation as a zip our own importer can read.
                      An archive you cannot leave is not an archive.</span>
              </div>
              <div>
                <strong>You choose who can open it</strong>
                <span>Let OkaZip hold the key and sign in anywhere, or hold it
                      yourself with a passkey and we keep bytes we cannot read.
                      Said plainly, at sign-up, either way.</span>
              </div>
            </section>

            <section className="lp-close">
              <h2>Your conversations outlive the phone they were on.</h2>
              {canGoogle && (
                <button onClick={() => void continueWith('google')} disabled={busy}>
                  {busy ? 'Waiting…' : 'Get started'}
                </button>
              )}
            </section>

            {/* Creating an account is one button now. Holding your own key is
                still on offer, but in the account panel, after there is an
                account to hold a key for — a passkey is a hard thing to
                explain to somebody who has not yet seen what this is.

                Two doors survive down here. A recovery code, because anyone
                who does take the key needs the lost-device path. And the
                passkey sign-up, when there is no Google to sign up with —
                otherwise a browser without it has no door at all — or when
                the URL asks for it, which is how the suites make an account
                against a stack where Google is configured but unusable by a
                robot. */}
            <footer className="lp-foot">
              <span>© OkaZip</span>
              <a href="/privacy">Privacy</a>
              <a href="/privacidade">Português</a>
              <a href="mailto:contato@okazip.com">contato@okazip.com</a>
              {canPasskey && (!canGoogle || wantsPasskey) && (
                <button className="lp-alt" onClick={createArchive} disabled={busy}>
                  Create one without Google
                </button>
              )}
              {canPasskey && (
                <button className="lp-alt" onClick={() => setRecoveryEntry('')}>
                  Use a recovery code
                </button>
              )}
            </footer>
          </>
        )}

        {cfg && !canPasskey && !canGoogle && (
          <>
            <p className="warn">
              Passkeys are unavailable here{cfg.reason ? `: ${cfg.reason}` : '.'}
            </p>
            <p className="fine">Signing in below uses the development path and is not secure.</p>
            <button onClick={signIn} disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </>
        )}

        {showSignIn && (
          <SignIn busy={busy} canGoogle={canGoogle} canApple={canApple}
                  canPasskey={otherWays} canRecovery={otherWays}
                  onClose={() => setShowSignIn(false)}
                  onApple={() => { setShowSignIn(false); void continueWith('apple'); }}
                  onGoogle={() => { setShowSignIn(false); void continueWith('google'); }}
                  onPasskey={() => { setShowSignIn(false); void signInWithPasskey(); }}
                  onRecovery={() => { setShowSignIn(false); setRecoveryEntry(''); }} />
        )}

        {recoveryEntry !== null && (
          <div className="recover">
            <label htmlFor="recovery-words">Your twenty-four words</label>
            <textarea id="recovery-words" rows={3} value={recoveryEntry}
                      onChange={(e) => setRecoveryEntry(e.target.value)} />
            <button disabled={busy} onClick={recoverWithCode}>
              {busy ? 'Unlocking…' : 'Unlock'}
            </button>
          </div>
        )}

        {error && <p className="err">{error}</p>}
      </div>
    );
  }

  return (
    // On a phone there is room for one pane, so the layout becomes a stack and
    // `reading` says which half is on screen. On a laptop both are visible and
    // the class changes nothing.
    // `reading` diz qual das duas colunas o telefone mostra, e a resposta é
    // "a janela principal" só quando é ela que tem o conteúdo. Era
    // `active || ask`, então com uma conversa aberta tocar em Media ou na
    // conta trocava o painel dentro de uma coluna escondida: nada acontecia.
    <div className={`app${panel === 'ask' || (panel === 'chats' && active) ? ' reading' : ''}`
                    + `${sideAsk && active ? ' with-ask' : ''}`}>
      {banner}
      {/* Um ícone, que sabe onde está. Houve dois durante uma tarde — um no
          cabeçalho da conversa, outro aqui — e dois ícones da mesma coisa
          são uma pergunta em vez de uma resposta. Dentro de uma conversa,
          este pergunta sobre ela; fora de qualquer conversa, sobre o
          arquivo todo. Onde o painel aparece muda com a largura — coluna
          ou ecrã — e disso trata o CSS, não isto. */}
      <Rail panel={sideAsk ? 'ask' : panel} onPanel={(p) => {
              if (p === 'ask' && active) { setSideAsk((o) => !o); return; }
              setPanel(p);
            }}
            name={account.displayName ?? account.email ?? null}
            avatarUrl={account.avatarUrl ?? null} />

      <aside className={`pane-${panel}`}>
        {panel === 'profile' && (
          <Profile inline accountKey={key} quota={account.quota}
                   start={profileAt}
                   onClose={() => { setProfileAt('menu'); setPanel('chats'); }}
                   onChange={() => { void refresh(); }} />
        )}

        {panel === 'media' && (
          <MediaPanel accountKey={key} chats={chats} titleOf={titleOf} active={active}
                      onPrime={() => { void primeServiceWorker(key); }}
                      onOpenChat={(c) => { setFocusSeq(undefined); setActive(c); setPanel('chats'); setSideAsk(false); }} />
        )}

        {(panel === 'chats' || panel === 'ask') && <>
        <header>
          <strong>OkaZip</strong>
          {/* Como sair do WhatsApp com o arquivo na mão morava aqui, atrás de
              três pontos. Continua a uma pergunta de distância — a lista
              vazia oferece o link, e é lá que está quem ainda não exportou
              nada — sem um segundo botão ao lado do "+". */}
          {/* Desenhado, não escrito: o glifo "+" fica pendurado no eixo
              matemático da fonte, sempre alto demais dentro do círculo. */}
          <button className="add" title="Import a WhatsApp export"
                  aria-label="Import a WhatsApp export"
                  onClick={() => pickFile.current?.()}>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 5.5v13M5.5 12h13" />
            </svg>
          </button>
        </header>

        <Importer
          pickRef={pickFile}
          startWith={pendingImport}
          existingFor={existingFor}
          onRunning={setImporting}
          onStarted={() => setPendingImport(null)}
          accountKey={key}
          onDone={async (m) => {
            rememberManifest(m);
            await refresh();
            await primeServiceWorker(key);
          }}
        />

        {installable && (
          <div className="offer">
            <div>
              <strong>Send exports straight here</strong>
              <span>Install OkaZip and it joins WhatsApp's share sheet — “Export
                    chat”, then OkaZip. No saving the file first.</span>
            </div>
            <div className="row gap">
              <button className="ghost" onClick={() => { dontAsk(); setInstallable(false); }}>
                Not now
              </button>
              <button onClick={() => { void install().finally(() => setInstallable(canInstall())); }}>
                Install
              </button>
            </div>
          </div>
        )}

        {chats.length > 0 && (
          <div className="find">
            {/* A lupa dentro do campo, como no WhatsApp: o campo passa a dizer
                o que é sem depender do texto de exemplo, que some assim que
                alguém digita a primeira letra. */}
            <svg className="glass" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4.5 4.5" />
            </svg>
            <input id="search" type="search" placeholder="Search chats"
                   autoComplete="off" value={chatQuery}
                   onChange={(e) => setChatQuery(e.target.value)} />
            {chatQuery && (
              <button className="clear" aria-label="Clear search"
                      onClick={() => setChatQuery('')}>×</button>
            )}
          </div>
        )}

        <ChatList chats={shownChats} accountKey={key} activeId={active?.chatId}
                  titleOf={titleOf} avatarOf={avatarOf}
                  onOpen={(c) => { setFocusSeq(undefined); setActive(c); setPanel('chats'); setSideAsk(false); }}
                  onHowTo={() => setShowHowTo(true)}
                  actions={{
                    onRename: (c, n) => { void rename(c, n); },
                    onPhoto: (c, f) => { void setAvatar(c, f); },
                    onExport: (c) => { void exportFromList(c); },
                    onRemove: (c) => { setRemoving(c); },
                    exportingId,
                    exportProgress: exporting,
                  }} />

        </>}
      </aside>

      {showHowTo && <HowTo onClose={() => setShowHowTo(false)} />}
      {joinAsk && (
        <div className="sheet" role="dialog" aria-modal="true" aria-label="Is this the same conversation?">
          <div className="sheet-body join">
            <h2>Is this the same conversation?</h2>
            <p>This export calls itself <strong>{joinAsk.incoming}</strong>, and your
               archive already has <strong>{joinAsk.existing}</strong>.</p>
            <p className="fine">Adding it joins the two: nothing already here is lost,
               and messages in both are kept once. Keeping it separate leaves a second
               conversation in the list.</p>
            <div className="row gap">
              <button className="ghost"
                      onClick={() => { joinAsk.resolve(false); setJoinAsk(null); }}>
                Keep separate
              </button>
              <button onClick={() => { joinAsk.resolve(true); setJoinAsk(null); }}>
                Add to “{joinAsk.existing}”
              </button>
            </div>
          </div>
        </div>
      )}

      <main>
        {/* A conversa com a IA mora aqui, não na coluna: é uma conversa, e
            conversa é o que esta janela mostra — é onde o WhatsApp põe a dele
            também. A coluna continua sendo a lista, para se trocar de assunto
            sem sair dela. */}
        {panel === 'ask' && (
          <AskPanel chats={chats} titleOf={titleOf} accountKey={key}
                    thread={askThread} onThread={setAskThread}
                    onBack={() => setPanel('chats')}
                    onEnable={() => { setProfileAt('ai'); setPanel('profile'); }} />
        )}

        {/* Hidden on a laptop, where the chat list is already beside this. */}
        {panel !== 'ask' && active && (
          <header className="chat-bar">
            <button className="back" onClick={() => setActive(null)}>
              <span aria-hidden="true">‹</span> Chats
            </button>
            <ChatTitle name={titleOf(active)} photo={avatarOf(active)} who={roster} />
            {/* Exportar e remover moraram aqui até a seta da linha existir.
                Duas portas para as mesmas duas ações, uma delas dentro da
                conversa que uma delas apaga — a da lista basta. */}
            <ChatSearch messages={messages} onJump={setFocusSeq} />
          </header>
        )}
        {panel !== 'ask' && !active && <div className="blank">Pick a chat, or import a <code>.zip</code>.</div>}
        {panel !== 'ask' && active && !messages && !error && <div className="blank">Decrypting {active.messageCount.toLocaleString()} messages…</div>}
        {error && <div className="blank err">{error}</div>}
        {panel !== 'ask' && active && messages && (
          <ChatView messages={messages} manifest={loadManifest()} mimeFor={mimeFor}
                    focusSeq={focusSeq} title={titleOf(active)}
                    myName={account.displayName ?? null} />
        )}
      </main>

      {removing && (
        <div className="sheet" role="dialog" aria-modal="true"
               aria-label="Remove conversation">
            <div className="sheet-body">
              <header>
                <h2>Remove “{titles[removing.chatId] ?? 'this conversation'}”?</h2>
                <button className="x" onClick={() => setRemoving(null)}
                        aria-label="Close">×</button>
              </header>
              <p className="fine">
                Everything that came with it goes — messages, photos, video,
                voice notes — and the space comes back, except for anything
                another conversation also refers to. This cannot be undone.
              </p>
              <button className="danger-btn" disabled={busy}
                      onClick={() => { void removeChat(removing); }}>
                Remove it
              </button>
              <button className="ghost" onClick={() => setRemoving(null)}>Cancel</button>
            </div>
          </div>
        )}

      {/* A terceira coluna. No telemóvel é um ecrã que entra pela direita —
          a mesma coisa, noutra forma, porque não há três colunas num
          telemóvel. */}
      {sideAsk && active && (
        <aside className="side-ask">
          <AskPanel chats={chats} titleOf={titleOf} accountKey={key}
                    pinned={titleOf(active)}
                    thread={chatAsk[active.chatId] ?? { said: [], turns: [], focus: titleOf(active) }}
                    onThread={(next) => setChatAsk((all) => ({ ...all, [active.chatId]: next }))}
                    onBack={() => setSideAsk(false)}
                    onEnable={() => { setSideAsk(false); setProfileAt('ai'); setPanel('profile'); }} />
        </aside>
      )}
    </div>
  );
}
